import { MobileDriver } from '../driver';
import { CanvasOpAndroid as CanvasOp, CanvasOpAndroid, Flags } from '@fullstory/mobile-flatbuffers';
import { CanvasRendererFactory } from '../canvasrender';
import { CanvasAndroid, View, CanvasPaint, Rect, Point, Frame } from '../view';
import { AndroidCanvasRendererFactory } from './androidcanvasrender';
import { PageTypeEnum } from '@fullstory/client-api';
import { DataUnpack } from '../dataunpack';

export class AndroidDriver extends MobileDriver<CanvasOp> {
  private _factory: CanvasRendererFactory<CanvasOp>;

  constructor() {
    super();
    this._factory = new AndroidCanvasRendererFactory();
  }

  type = PageTypeEnum.PageTypeAndroid;
  // prettier-ignore
  defaultCanvasDefintion = Int8Array.from([1, 1, 20, 18, 13, 50, 2, 35, 8, 2, 21, 6, 10, 11, 14, 9, 15, 10, 6, 8, 7, 9, 4, 8, 5, 9,
    22, 5, 82, 1, 81, 4, 89, 2, 80, 34, 86, 66, 88, 6, 83, 4, 84, 2, 93, 6, 94, 2, 90, 2, 91, 9, 85, 2, 95, 2, 96,
    2, 97, 82, 87, 11, 92, 1]);
  isBlockedFlag = Flags.BLOCKED;
  isWebViewFlag = Flags.WEBVIEW;

  rendererFactory(): CanvasRendererFactory<CanvasOp> {
    return this._factory;
  }

  reprocessCanvas(view: View) {
    view.clipsChildrenToBounds = view.flag(Flags.VIEW_GROUP_CLIP_CHILDREN);
    // TODO(mmastrac): This isn't the correct logic here: we need to consider dispatchDraw/etc
    // If we have any drawChild calls, we assume that they are correctly positioned in the event stream
    // and don't generate them
    let hasDrawChild = false;
    for (let i = 0; i < view.canvas.length; i++) {
      const entry = view.canvas[i];
      if (entry.op == CanvasOp.DRAW_CHILD) {
        hasDrawChild = true;
      }

      if (entry.op == CanvasOp.CLIP_RECT) {
        const src = entry.src;
        if (
          src.x1 != 0 ||
          src.y1 != 0 ||
          src.x2 != view.position.width() ||
          src.y2 != view.position.height()
        ) {
          view.hasCrop += `[${src.x1},${src.y1},${src.x2},${src.y2}]`;
        }
      }

      if (entry.op == CanvasOp.DRAW_TEXT) {
        view.textContent += entry.text;
      }
    }
    if (!hasDrawChild) {
      for (let i = 0; i < view.children.length; i++) {
        const canvas = new CanvasAndroid();
        canvas.op = CanvasOp.DRAW_CHILD;
        canvas.viewId = view.children[i].uniqueId;
        view.canvas.push(canvas);
      }
    }
  }

  unpackCanvasItem(frame: Frame, canvasJson: any): CanvasAndroid {
    const canvas = new CanvasAndroid();
    canvas.op = canvasJson.op;

    canvas.bitmap = canvasJson.bitmap;
    canvas.text = canvasJson.text;
    canvas.path = canvasJson.path;

    if (canvasJson.srczero)
      canvas.src = new Rect(0, 0, canvasJson.srczero[0], canvasJson.srczero[1]);
    else if (canvasJson.src) canvas.src = DataUnpack.unpackRectArray(canvasJson.src);
    else canvas.src = Rect.ZERO();

    if (canvasJson.destzero)
      canvas.dest = new Rect(0, 0, canvasJson.destzero[0], canvasJson.destzero[1]);
    else if (canvasJson.dest) canvas.dest = DataUnpack.unpackRectArray(canvasJson.dest);
    else canvas.dest = Rect.ZERO();

    canvas.matrix = DataUnpack.unpackMatrixArray(canvasJson.matrix);
    if (canvasJson.point) canvas.point = DataUnpack.unpackPointArray(canvasJson.point);
    else if (canvasJson.pointf) canvas.point = DataUnpack.unpackPointArray(canvasJson.pointf);
    else canvas.point = Point.ZERO();
    canvas.value = canvasJson.value || 0;
    canvas.viewId = DataUnpack.unpackObjectId(canvasJson.viewid, canvasJson.viewid64);
    canvas.flag1 = canvasJson.flag1 || 0;

    // Only DRAW_ calls and SAVE_LAYER have paint
    canvas.paint = new CanvasPaint();
    if (canvasJson.paintnull === null || canvas.op == CanvasOpAndroid.DRAW_VIEW) {
      canvas.paint.isDefault = true;
      canvas.paint.strokeWidth = 0;
      canvas.paint.style = 0;
      canvas.paint.porterDuffColor = null;
      canvas.paint.porterDuffColorMode = null;
      canvas.paint.porterDuffXferMode = null;
      canvas.paint.color = 'transparent';
      // TODO(mmastrac): This doesn't match the 'transparent' above on purpose for some reason
      // TODO(mmastrac): When we are rewriting this code, we need to keep that in mind
      canvas.paint.alpha = 1;
      canvas.paint.bitmap = null;
      canvas.paint.shader = null;
      canvas.paint.fillRule = 'nonzero';
    } else {
      canvas.paint.strokeWidth = canvasJson.paintstrokewidth || 0;
      canvas.paint.style = canvasJson.paintstyle || 0;

      canvas.paint.porterDuffColorMode = DataUnpack.unpackPorterDuffMode(
        canvasJson.paintporterduffcolormode,
      );
      if (canvas.paint.porterDuffColorMode)
        canvas.paint.porterDuffColor = DataUnpack.unpackCssColor(canvasJson.paintporterduffcolor);

      canvas.paint.porterDuffXferMode = DataUnpack.unpackPorterDuffMode(
        canvasJson.paintporterduffxfermode,
      );

      // fillRule defaults to 'nonzero': non-zero winding:
      // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/fill
      // Path fill type for Android:
      // 0 - WINDING; 1 - EVEN_ODD, 2 - INVERSE_EVEN_ODD, 3 - INVERSE_WINDING
      // https://developer.android.com/reference/android/graphics/Path.FillType
      // TODO(sabrina): only checking winding vs evenodd right now, ignoring inverse types
      if (canvasJson.pathfilltype === 1 || canvasJson.pathfilltype === 2)
        canvas.paint.fillRule = 'evenodd';
      else canvas.paint.fillRule = 'nonzero';

      if (canvasJson.paintcolor) {
        canvas.paint.color = DataUnpack.unpackCssColor(canvasJson.paintcolor);
        canvas.paint.alpha = (((canvasJson.paintcolor || 0) >> 24) & 0xff) / 255;
      } else {
        canvas.paint.color = DataUnpack.unpackCssColor(0);
        canvas.paint.alpha = 0;
      }

      if (canvasJson.paintbitmap) {
        canvas.paint.bitmap = canvasJson.paintbitmap;
        canvas.paint.tileX = DataUnpack.unpackTileMode(canvasJson.painttilex);
        canvas.paint.tileY = DataUnpack.unpackTileMode(canvasJson.painttiley);
        canvas.paint.shaderMatrix = DataUnpack.unpackMatrixArray(canvasJson.paintshadermatrix);
      }

      canvas.paint.shader = canvasJson.paintshader;

      if (canvas.op == CanvasOpAndroid.DRAW_TEXT) {
        canvas.textPrivate = canvasJson.painttextprivate === null;
        canvas.paint.size = canvasJson.painttextsize || 0;
        canvas.paint.textStyle = canvasJson.painttextstyle || 0;
        canvas.paint.align = canvasJson.painttextalign || 0;
        canvas.paint.textBounds = DataUnpack.unpackPointArray(canvasJson.painttextbounds);
        canvas.paint.typeface = canvasJson.painttypeface;
        if (canvas.paint.typeface) {
          frame.fonts[canvas.paint.typeface.getName()] = canvas.paint.typeface;
        }
      }
    }

    if (canvasJson.op == 'SAVE_LAYER_ALPHA') {
      // Unpack flag1 into flag1/alpha
      canvas.paint = new CanvasPaint();
      canvas.paint.alpha = canvas.flag1 & 0xff;
      canvas.flag1 >>= 8;
    }

    return canvas;
  }
}
